import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
  Rating,
  FormHelperText,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SellerReview } from "../../../../services/sellerReviews";
import {
  ReviewCriteriaField,
  ReviewCriteriaRatingFields,
} from "../../../../components/ReviewCriteriaRatings";

interface ReviewEditDialogProps {
  review: SellerReview | null;
  open: boolean;
  onClose: () => void;
  onSave: (reviewId: string, updates: any) => Promise<void>;
}

const ReviewEditDialog: React.FC<ReviewEditDialogProps> = ({ review, open, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<SellerReview>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (review) {
      setFormData({
        display_seller_name: review.display_seller_name || review.seller?.company_name || review.seller?.full_name || "",
        display_reviewer_name: review.display_reviewer_name || review.reviewer?.full_name || "",
        display_date: review.display_date
          ? review.display_date.slice(0, 10)
          : review.created_at
          ? review.created_at.slice(0, 10)
          : "",
        reliability_rating: review.reliability_rating,
        response_speed_rating: review.response_speed_rating,
        engineer_efficiency_rating: review.engineer_efficiency_rating,
        honesty_rating: review.honesty_rating,
        comment: review.comment || "",
      });
    } else {
      setFormData({});
    }
    setErrors({});
  }, [review]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const validateRating = (value: number | null | undefined, field: string) => {
      if (value == null || value === 0) {
        newErrors[field] = t("admin.sellerReviews.ratingRequired", { defaultValue: "Rating is required" });
      } else if (value < 0.5 || value > 5) {
        newErrors[field] = t("admin.sellerReviews.ratingOutOfRange", {
          defaultValue: "Rating must be between 0.5 and 5",
        });
      }
    };
    validateRating(formData.reliability_rating ?? null, "reliability_rating");
    validateRating(formData.response_speed_rating ?? null, "response_speed_rating");
    validateRating(formData.engineer_efficiency_rating ?? null, "engineer_efficiency_rating");
    validateRating(formData.honesty_rating ?? null, "honesty_rating");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !review) return;
    setSaving(true);
    try {
      const { overall_rating: _overall, ...updates } = formData;
      await onSave(review.id, updates);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleRatingChange = (field: ReviewCriteriaField, value: number | null) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      const ratings = [
        next.reliability_rating,
        next.response_speed_rating,
        next.engineer_efficiency_rating,
        next.honesty_rating,
      ].filter((r): r is number => r != null && r > 0);
      if (ratings.length === 4) {
        next.overall_rating = ratings.reduce((sum, r) => sum + r, 0) / 4;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleTextChange = (field: keyof SellerReview, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!review) return null;

  const sellerName = review.display_seller_name || review.seller?.company_name || review.seller?.full_name || t("admin.sellerReviews.unknownSeller", { defaultValue: "Unknown Seller" });
  const reviewerName = review.display_reviewer_name || review.reviewer?.full_name || t("admin.sellerReviews.unknownReviewer", { defaultValue: "Unknown Reviewer" });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          {t("admin.sellerReviews.editReview", { defaultValue: "Edit Review" })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("admin.sellerReviews.reviewFor", { defaultValue: "Review for" })}: {sellerName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("admin.reviewedBy", { defaultValue: "Reviewed by" })}: {reviewerName}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Editable identity fields */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
              {t("admin.sellerReviews.reviewInfo", { defaultValue: "Review Info" })}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t("admin.sellerReviews.sellerName", { defaultValue: "Seller Name" })}
                  value={formData.display_seller_name || ""}
                  onChange={(e) => handleTextChange("display_seller_name", e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t("admin.sellerReviews.reviewerName", { defaultValue: "Reviewed By" })}
                  value={formData.display_reviewer_name || ""}
                  onChange={(e) => handleTextChange("display_reviewer_name", e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t("admin.sellerReviews.reviewDate", { defaultValue: "Review Date" })}
                  type="date"
                  value={formData.display_date || ""}
                  onChange={(e) => handleTextChange("display_date", e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
              {t("admin.sellerReviews.individualRatings", { defaultValue: "Individual Ratings" })}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <ReviewCriteriaRatingFields
                values={formData}
                errors={errors}
                onChange={handleRatingChange}
              />
            </Box>
          </Box>

          {/* Overall rating is a GENERATED column — show as read-only computed value */}
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
              {t("admin.sellerReviews.overallRating", { defaultValue: "Overall Rating" })}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Rating
                value={
                  [
                    formData.reliability_rating,
                    formData.response_speed_rating,
                    formData.engineer_efficiency_rating,
                    formData.honesty_rating,
                  ].every(Boolean)
                    ? ((
                        (formData.reliability_rating || 0) +
                        (formData.response_speed_rating || 0) +
                        (formData.engineer_efficiency_rating || 0) +
                        (formData.honesty_rating || 0)
                      ) / 4)
                    : null
                }
                precision={0.5}
                size="large"
                readOnly
                sx={{ color: "#FFD700" }}
              />
              <Typography variant="h5" fontWeight={700}>
                {
                  [
                    formData.reliability_rating,
                    formData.response_speed_rating,
                    formData.engineer_efficiency_rating,
                    formData.honesty_rating,
                  ].every(Boolean)
                    ? ((
                        (formData.reliability_rating || 0) +
                        (formData.response_speed_rating || 0) +
                        (formData.engineer_efficiency_rating || 0) +
                        (formData.honesty_rating || 0)
                      ) / 4).toFixed(2)
                    : "–"
                } / 5
              </Typography>
            </Stack>
            <FormHelperText sx={{ ml: 1, mt: 1 }}>
              {t("admin.sellerReviews.overallRatingAuto", { defaultValue: "Auto-computed from the four ratings above" })}
            </FormHelperText>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
              {t("reviews.customerFeedback", { defaultValue: "Customer Feedback" })}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={t("admin.sellerReviews.commentPlaceholder", { defaultValue: "Edit the review comment..." })}
              value={formData.comment || ""}
              onChange={(e) => handleTextChange("comment", e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={saving}>
          {t("common.cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            px: 4,
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 2,
          }}
        >
          {saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save", { defaultValue: "Save Changes" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewEditDialog;
